// Kitchen stations: counters, crates, stoves, blenders, prep, sink, etc.
import { Node, Light } from '../engine/renderer.js';
import { col, lerp } from '../engine/math.js';
import * as M from './models.js';
import { Vessel, Extinguisher, Ingredient } from './items.js';
import { INGREDIENTS } from './data.js';

export const COUNTER_H = 0.9;
const SEAL_HEAD_UP = 0.62, SEAL_HEAD_DOWN = 0.45;

const KIND_BY_CHAR = {
  '#': 'counter', T: 'bar', K: 'stove', k: 'stove', B: 'blender', b: 'blender', C: 'chop', G: 'grind', S: 'sink', R: 'rack',
  X: 'bin', Z: 'carbonator', E: 'ext', H: 'chute', D: 'cups', V: 'sealer', Y: 'straws', '>': 'conveyor', '<': 'conveyor', '^': 'conveyor', '|': 'conveyor', W: 'wall',
};
export function stationKind(ch, crates) {
  if (crates && crates[ch]) return 'crate';
  return KIND_BY_CHAR[ch] || null;
}

export class Station {
  constructor(game, x, y, ch, kind) {
    this.game = game;
    this.x = x;
    this.y = y;
    this.ch = ch;
    this.kind = kind;
    const [wx, wz] = game.tileToWorld(x, y);
    this.wx = wx;
    this.wz = wz;
    this.top = [wx, COUNTER_H, wz];
    this.item = null;
    this.node = new Node();
    this.node.position = [wx, COUNTER_H, wz];
    this.anchor = new Node();
    this.node.add(this.anchor);
    this.fire = null; // {hp, t, node}
    this.prog = 0;
    this.highlight = 0;
    this.dir = [1, 0];
    game.scene.add(this.node);
    this._build();
  }
  get interactable() {
    return this.kind !== 'wall';
  }
  get flammable() {
    return !['bar', 'wall', 'crate', 'chute', 'conveyor', 'sink', 'bin', 'ext', 'rack', 'cups', 'straws'].includes(this.kind);
  }
  _build() {
    const g = this.game, th = g.theme;
    const add = (mesh, y = 0, opts) => {
      const n = new Node(mesh, opts);
      n.position[1] = y;
      this.node.add(n);
      return n;
    };
    switch (this.kind) {
      case 'stove': {
        add(M.stationMesh('stove', th));
        this.ring = add(M.stoveRingMesh(), 0);
        this.ring.tint = [0.2, 0.2, 0.25, 1];
        this.light = new Light({ position: [this.wx, COUNTER_H + 0.4, this.wz], color: '#ff7a2a', intensity: 0, range: 3.2 });
        g.scene.lights.push(this.light);
        this.anchor.position = [0, 0.04, 0];
        if (this.ch === 'K') this.setItem(g.registerVessel(new Vessel('kettle'), this));
        break;
      }
      case 'blender': {
        add(M.stationMesh('blender', th));
        this.anchor.position = [0, 0.18, 0];
        if (this.ch === 'B') this.setItem(g.registerVessel(new Vessel('blender'), this));
        break;
      }
      case 'chop':
        add(M.stationMesh('chop', th));
        this.anchor.position = [-0.06, 0.064, 0.03];
        this.knife = add(M.knifeMesh(th), 0);
        this.knife.scale = [1.15, 1.15, 1.15];
        this.knifeK = 0; // 0 resting on the board, 1 chopping
        this.knifeSway = 0;
        this.knifeLift = 0; // how far the handle is up (0 down on the board, 1 at the top of a chop)
        this._knifePose(0, 0);
        break;
      case 'grind':
        add(M.stationMesh('grind', th));
        this.pestle = add(M.pestleMesh(), 0.05);
        this.pestle.rotation = [0.3, 0, 0.3];
        this.anchor.position = [0, 0.09, 0];
        break;
      case 'sink':
        add(M.stationMesh('sink', th));
        this.dirty = 0;
        this.anchor.position = [0, 0.03, 0];
        this.dirtyNodes = [];
        break;
      case 'rack':
        add(M.stationMesh('rack', th));
        this.clean = 0;
        this.cupNodes = [];
        break;
      case 'bin':
        add(M.stationMesh('bin', th));
        this.arcT = 0;
        this.zapT = 0;
        this.light = new Light({ position: [this.wx, COUNTER_H + 0.75, this.wz], color: '#9fdcff', intensity: 0.7, range: 2.6 });
        g.scene.lights.push(this.light);
        break;
      case 'carbonator':
        add(M.stationMesh('carbonator', th));
        this.anchor.position = [0, 0.09, 0.02];
        this.light = new Light({ position: [this.wx, COUNTER_H + 0.7, this.wz], color: '#7affd8', intensity: 0.6, range: 2.2 });
        g.scene.lights.push(this.light);
        break;
      case 'ext':
        add(M.stationMesh('extinguisherHook', th));
        this.anchor.position = [0, 0.04, 0];
        this.setItem(new Extinguisher());
        break;
      case 'crate': {
        add(M.stationMesh('crate', th));
        const id = g.level.crates[this.ch];
        this.ingId = id;
        // display a few of the ingredient inside the crate
        for (let i = 0; i < 3; i++) {
          const n = new Node(M.ingredientMesh(id, 'raw'));
          n.position = [(i - 1) * 0.24, 0.17, (i % 2) * 0.14 - 0.07];
          n.rotation = [0, i * 1.7, 0];
          n.scale = [1.1, 1.1, 1.1];
          this.node.add(n);
        }
        const d = INGREDIENTS[id];
        add(M.crateBandMesh(d.color));
        this.light = null;
        if (d.glow && d.glow > 1) {
          this.light = new Light({ position: [this.wx, COUNTER_H + 0.5, this.wz], color: d.color, intensity: 1.2, range: 2.5 });
          g.scene.lights.push(this.light);
        }
        break;
      }
      case 'chute':
        add(M.stationMesh('chute', th));
        this.timer = 1;
        this.queue = 0;
        break;
      case 'conveyor': {
        this.node.position[1] = COUNTER_H - 0.06;
        add(M.stationMesh('conveyor', th), 0);
        this.arrows = add(M.stationMesh('arrows', th), 0);
        const d = { '>': [1, 0], '<': [-1, 0], '^': [0, -1], '|': [0, 1] }[this.ch];
        this.dir = d;
        const rot = Math.atan2(d[0], d[1]) - Math.PI / 2;
        this.node.rotation = [0, rot, 0];
        this.anchor.rotation = [0, -rot, 0];
        this.anchor.position = [0, 0.04, 0];
        this.beltT = 0;
        break;
      }
      case 'bar':
        this.anchor.position = [0, 0, 0.12];
        break;
      case 'cups':
        add(M.stationMesh('cups', th));
        break;
      case 'straws':
        add(M.stationMesh('straws', th));
        break;
      case 'sealer':
        add(M.stationMesh('sealer', th));
        this.head = add(M.stationMesh('sealerHead', th), SEAL_HEAD_UP);
        this.head.position[2] = 0.02;
        this.anchor.position = [0, 0.07, 0.02];
        this.light = new Light({ position: [this.wx, COUNTER_H + 0.5, this.wz], color: th.accent, intensity: 0.4, range: 2 });
        g.scene.lights.push(this.light);
        break;
    }
  }
  setItem(item) {
    this.item = item;
    if (item) {
      item.holder = this;
      this.anchor.add(item.node);
      item.node.position = [0, 0, 0];
      item.node.rotation = [0, 0, 0];
      item.node.scale = [1, 1, 1];
    }
  }
  takeItem() {
    const it = this.item;
    this.item = null;
    if (it) {
      this.anchor.remove(it.node);
      it.holder = null;
      if (it._lit) {
        // it was glowing as a belt target: back to normal off the belt
        it.node.traverse((n) => n.mesh && !n.fx && (n.emissive = [0, 0, 0]));
        it._lit = false;
        it.highlight = 0;
      }
    }
    this.prog = 0;
    return it;
  }
  accepts(item) {
    if (this.fire) return false;
    switch (this.kind) {
      case 'counter': case 'chop': case 'grind': case 'ext': case 'conveyor':
        return true;
      case 'stove':
        return item.kind === 'vessel' && item.app === 'kettle';
      case 'blender':
        return item.kind === 'vessel' && item.app === 'blender';
      case 'carbonator':
        return item.kind === 'cup' && !!item.drink && !item.dirty;
      case 'sealer':
        return item.kind === 'bobacup' && item.sealable;
      default:
        return false;
    }
  }
  setRackCount(n) {
    this.clean = n;
    while (this.cupNodes.length < n && this.cupNodes.length < 8) {
      const i = this.cupNodes.length;
      const c = new Node(M.cupMesh(false));
      const row = i < 4 ? 0 : 1;
      c.position = [-0.24 + (i % 4) * 0.16, row ? 0.49 : 0.03, row ? -0.16 : 0.06];
      c.scale = [0.8, 0.8, 0.8];
      this.node.add(c);
      this.cupNodes.push(c);
    }
    while (this.cupNodes.length > Math.min(n, 8)) this.node.remove(this.cupNodes.pop());
  }
  setSinkCount(n) {
    this.dirty = n;
    while (this.dirtyNodes.length < n && this.dirtyNodes.length < 6) {
      const i = this.dirtyNodes.length;
      const c = new Node(M.cupMesh(true));
      c.position = [-0.13 + (i % 3) * 0.13, 0.02 + Math.floor(i / 3) * 0.05, (i % 2) * 0.12 - 0.06];
      c.rotation = [0.3 * (i % 2), i, 0.2];
      c.scale = [0.85, 0.85, 0.85];
      this.node.add(c);
      this.dirtyNodes.push(c);
    }
    while (this.dirtyNodes.length > Math.min(n, 6)) this.node.remove(this.dirtyNodes.pop());
  }
  /** Zapper: flickering electric arcs between the coil tips; on a zap they all strike the grate. */
  zap() {
    this.zapT = 0.4;
    this.game.sfx('zap');
    this.game._ev('z', [this.game.stations.indexOf(this)]);
  }
  _arcs(dt, t) {
    const P = this.game.scene.particles[0];
    const base = [this.wx, COUNTER_H, this.wz];
    const tip = (i) => M.ZAPPER_TIPS[i].map((v, k) => v + base[k]);
    // lightning: a few jagged kinks, then densely sampled soft dots so it reads as a glowing line
    const bolt = (a, b2, n, jit, bright) => {
      const kinks = [a];
      for (let i = 1; i < n; i++) {
        const f = i / n, j = Math.sin(f * Math.PI) * jit;
        kinks.push([a[0] + (b2[0] - a[0]) * f + (Math.random() - 0.5) * j, a[1] + (b2[1] - a[1]) * f + (Math.random() - 0.5) * j + Math.sin(f * Math.PI) * 0.05, a[2] + (b2[2] - a[2]) * f + (Math.random() - 0.5) * j]);
      }
      kinks.push(b2);
      const life = 0.06 + Math.random() * 0.05;
      for (let k = 1; k < kinks.length; k++) {
        const p0 = kinks[k - 1], p1 = kinks[k];
        const len = Math.hypot(p1[0] - p0[0], p1[1] - p0[1], p1[2] - p0[2]);
        const steps = Math.max(2, Math.ceil(len / 0.014));
        for (let i = 0; i < steps; i++) {
          const f = i / steps;
          const q = [p0[0] + (p1[0] - p0[0]) * f, p0[1] + (p1[1] - p0[1]) * f, p0[2] + (p1[2] - p0[2]) * f];
          P.emit({ pos: q, vel: [0, 0, 0], life, size: 0.045 * bright, size1: 0.03 * bright, color: [0.35, 0.65, 1], alpha: 0.9 * bright, type: 0 });
          P.emit({ pos: q, vel: [0, 0, 0], life, size: 0.016 * bright, size1: 0.012, color: [0.9, 0.97, 1], alpha: 2.2 * bright, type: 0 });
        }
      }
    };
    this.arcT -= dt;
    if (this.zapT > 0) this.zapT -= dt;
    const zapping = this.zapT > 0;
    if (this.arcT <= 0) {
      this.arcT = zapping ? 0.03 : 0.05 + Math.random() * 0.08;
      if (zapping) {
        const grate = [base[0], base[1] + 0.2, base[2]];
        for (let i = 0; i < 3; i++) bolt(tip(i), grate, 6, 0.16, 1.3);
      } else {
        // one or two wandering arcs between random coil tips
        const n = Math.random() < 0.35 ? 2 : 1;
        for (let k = 0; k < n; k++) {
          const i = (Math.random() * 3) | 0, j = (i + 1 + ((Math.random() * 2) | 0)) % 3;
          if (Math.random() < 0.25) {
            // a stray arc reaching up into the air
            const a = tip(i);
            bolt(a, [a[0] + (Math.random() - 0.5) * 0.3, a[1] + 0.25 + Math.random() * 0.2, a[2] + (Math.random() - 0.5) * 0.3], 4, 0.12, 0.8);
          } else bolt(tip(i), tip(j), 6, 0.18, 1);
        }
      }
    }
    if (this.light) this.light.intensity = zapping ? 5 + Math.random() * 4 : 0.5 + Math.random() * 0.9;
  }
  /** Where the knife is: lying on the back of the board (k = 0), or up over the ingredient mid-chop (k = 1). */
  _knifePose(k, lift) {
    const [ax, , az] = this.anchor.position;
    const e = k * k * (3 - 2 * k);
    // resting: flat on its side along the back, at a slight angle
    const rest = { p: [0.29, 0.079, -0.13], r: [-Math.PI / 2, 0.22, 0] };
    // chopping: stood on its edge across the food, rocking on its tip on the far side, the handle towards the
    // chef's hand rising and falling
    const [dx, dz] = this.knifeDir || [0, 1];
    let yaw = Math.atan2(dz, -dx);
    while (yaw - rest.r[1] > Math.PI) yaw -= Math.PI * 2;
    while (yaw - rest.r[1] < -Math.PI) yaw += Math.PI * 2;
    const sw = this.knifeSway;
    const chop = { p: [ax - dx * 0.14 - dz * sw, 0.064, az - dz * 0.14 + dx * sw], r: [0, yaw, -0.08 - 0.62 * lift] };
    this.knife.position = rest.p.map((v, i) => lerp(v, chop.p[i], e));
    this.knife.rotation = rest.r.map((v, i) => lerp(v, chop.r[i], e));
  }
  /** Chopping: the knife rocks down through the ingredient in time with the chop sound, bits fly, the food squashes. */
  _chopAnim(dt, t) {
    const it = this.item;
    const on = !!(it && it.kind === 'ing' && this.useT > 0);
    this.knifeK = Math.max(0, Math.min(1, this.knifeK + (on ? dt * 9 : -dt * 5)));
    this.knifeSway = Math.sin(t * 2.6) * 0.07; // working its way along the food, slice by slice
    // the handle points to whoever's chopping (or towards the camera, if we can't tell)
    const who = this.game.chefs.find((c) => c.job === this);
    if (who) {
      const d = Math.hypot(who.x - this.wx, who.z - this.wz) || 1;
      this.knifeDir = [(who.x - this.wx) / d, (who.z - this.wz) / d];
    } else if (!on) this.knifeDir = null;
    if (on) {
      // one chop every 0.17s (the chop sound's beat): down at u = 0, eases up, then comes down hard
      if (!this.chopping) this.chopU = 1; // the first one lands straight away
      this.chopping = true;
      this.chopU += dt / 0.17;
      if (this.chopU >= 1) {
        this.chopU %= 1;
        this._chopHit(it);
      }
      const u = this.chopU;
      this.knifeLift = u < 0.55 ? Math.sin((u / 0.55) * Math.PI * 0.5) : Math.cos(((u - 0.55) / 0.45) * Math.PI * 0.5);
    } else {
      this.chopping = false;
      this.knifeLift = Math.max(0, this.knifeLift - dt * 6);
    }
    this._knifePose(this.knifeK, this.knifeLift * this.knifeK);
    // the food squashes a little on each chop and springs back
    this.squash = Math.max(0, (this.squash || 0) - dt * 9);
    if (this._sqItem && this._sqItem !== it && this._sqItem.node) this._sqItem.node.scale = [1, 1, 1]; // (taken off mid-squash)
    this._sqItem = it;
    if (it && it.node) {
      const s = this.squash;
      it.node.scale = [1 + s * 0.1, 1 - s * 0.2, 1 + s * 0.1];
    }
  }
  _chopHit(it) {
    this.squash = 1;
    const g = this.game, d = it.def || INGREDIENTS[it.id];
    if (!d || !g.scene.particles) return;
    const [ax, ay, az] = this.anchor.position;
    const [dx, dz] = this.knifeDir || [0, 1];
    const x = this.wx + ax - dz * this.knifeSway, y = COUNTER_H + ay + 0.06, z = this.wz + az + dx * this.knifeSway;
    const c1 = col(d.color), c2 = col(d.accent);
    for (let i = 0; i < 4; i++) {
      const a = Math.random() * Math.PI * 2, v = 0.6 + Math.random() * 0.9;
      g.scene.particles[0].emit({ pos: [x, y, z], vel: [Math.cos(a) * v, 1.1 + Math.random() * 1.3, Math.sin(a) * v], life: 0.45 + Math.random() * 0.2, size: 0.035 + Math.random() * 0.02, size1: 0.008, color: i % 2 ? c1 : c2, alpha: 1, gravity: 7, type: 0 });
    }
    // a glint off the edge
    g.scene.particles[0].emit({ pos: [x + dx * 0.05, y + 0.02, z + dz * 0.05], vel: [0, 0.3, 0], life: 0.16, size: 0.05, size1: 0, color: [1, 1, 1], alpha: 1.4, type: 1 });
  }
  update(dt, t) {
    const g = this.game;
    // highlight glow
    const h = this.highlight;
    if (h > 0 || this._hl) {
      for (const n of this.node.children) if (n.mesh && !n.fx && n !== this.ring) n.emissive = [h * 0.1, h * 0.12, h * 0.16];
      this._hl = h > 0;
    }
    this.highlight = Math.max(0, h - dt * 6);
    if (this.kind === 'stove') {
      const v = this.item;
      const heating = v && v.contents.length > 0 && !this.fire && !v.burnt;
      const target = heating ? 1 : 0;
      this.heat = lerp(this.heat || 0, target, 1 - Math.exp(-dt * 4));
      const warn = v && v.done && v.app === 'kettle' && v.over > 3;
      const c = warn ? [1.0, 0.2, 0.1] : [1.0, 0.45, 0.15];
      const hh = this.heat;
      this.ring.tint = [0.15 + c[0] * hh * 3, 0.15 + c[1] * hh * 3, 0.2 + c[2] * hh * 3, 1];
      this.ring.emitMul = 1 + hh * 3;
      this.light.intensity = hh * (4 + Math.sin(t * 20) * 0.6 + Math.sin(t * 7.3) * 0.5);
      this.light.color = warn ? col('#ff3a2a') : col('#ff8a3a');
    }
    if (this.kind === 'grind' && this.pestle) {
      const active = this.useT > 0;
      this.pestle.rotation[1] += dt * (active ? 12 : 0.3);
      this.pestle.position[1] = 0.05 + (active ? Math.abs(Math.sin(t * 14)) * 0.05 : 0);
    }
    if (this.kind === 'chop') this._chopAnim(dt, t);
    if (this.useT > 0) this.useT -= dt;
    if (this.kind === 'bin') this._arcs(dt, t);
    if (this.kind === 'conveyor') {
      this.arrows.position[0] = ((t * 0.6) % 0.5) - 0.25;
    }
    if (this.kind === 'sealer') {
      // the press head comes down while a cup is being sealed, then lifts again
      const cup = this.item;
      const pressing = cup && cup.kind === 'bobacup' && !cup.sealed && cup.sealProg > 0;
      const want = pressing ? SEAL_HEAD_DOWN + Math.sin(t * 40) * 0.004 : SEAL_HEAD_UP;
      this.head.position[1] = lerp(this.head.position[1], want, 1 - Math.exp(-dt * (pressing ? 10 : 6)));
      this.light.intensity = pressing ? 1.8 + Math.sin(t * 25) * 0.4 : 0.4;
    }
    if (this.kind === 'carbonator') {
      const cup = this.item;
      this.light.intensity = 0.6 + (cup && cup.drink && !cup.fizzy ? 1.5 + Math.sin(t * 15) * 0.5 : 0);
    }
  }
}
